import { Box, Typography, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import HistoryIcon from '@mui/icons-material/History';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MYPAGE_MENU_ITEMS, type MypageMenuId } from '@/constants/mypageMenu';

interface MenuListCardProps {
    vendorPath: string;
    onRecentProductsClick: () => void;
}

/**
 * 메뉴 목록 카드
 * - 주문 내역, 미송 내역, 샘플 내역, 최근 본 상품
 */
const MenuListCard = ({ vendorPath, onRecentProductsClick }: MenuListCardProps) => {
    const navigate = useNavigate();

    // 메뉴 ID별 아이콘 매핑
    const menuIcons: Record<MypageMenuId, React.ReactNode> = {
        home: <HomeOutlinedIcon sx={{ fontSize: 22, color: '#666' }} />,
        subscription: <CardMembershipIcon sx={{ fontSize: 22, color: '#666' }} />,
        orders: <ReceiptLongIcon sx={{ fontSize: 22, color: '#666' }} />,
        returns: <AssignmentReturnIcon sx={{ fontSize: 22, color: '#666' }} />,
        backorder: <LocalShippingOutlinedIcon sx={{ fontSize: 22, color: '#666' }} />,
        sample: <CardGiftcardIcon sx={{ fontSize: 22, color: '#666' }} />,
        recent: <HistoryIcon sx={{ fontSize: 22, color: '#666' }} />,
        cashCharge: <PaidIcon sx={{ fontSize: 22, color: '#666' }} />,
        cashHistory: <ReceiptOutlinedIcon sx={{ fontSize: 22, color: '#666' }} />,
        depositHistory: <PaymentsOutlinedIcon sx={{ fontSize: 22, color: '#666' }} />,
    };

    // 메뉴 ID별 클릭 핸들러
    const menuHandlers: Record<MypageMenuId, () => void> = {
        home: () => navigate(`/${vendorPath}/mypage`),
        subscription: () => navigate(`/${vendorPath}/settings/subscription`),
        orders: () => navigate(`/${vendorPath}/orders`),
        returns: () => navigate(`/${vendorPath}/returns`),
        backorder: () => navigate(`/${vendorPath}/backorder`),
        sample: () => navigate(`/${vendorPath}/sample`),
        recent: onRecentProductsClick,
        cashCharge: () => navigate(`/${vendorPath}/cash/charge`),
        cashHistory: () => navigate(`/${vendorPath}/cash/history`),
        depositHistory: () => navigate(`/${vendorPath}/deposit/history`),
    };

    // 마이페이지 홈에서는 'home' 메뉴 제외
    const menuItems = MYPAGE_MENU_ITEMS.filter((item) => item.id !== 'home').map((item) => ({
        ...item,
        icon: menuIcons[item.id],
        onClick: menuHandlers[item.id],
    }));

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: 3,
                border: '1px solid #eee',
                overflow: 'hidden',
            }}
        >
            {menuItems.map((item, index) => (
                <Box key={item.label}>
                    <Box
                        onClick={item.onClick}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: { xs: 2, sm: 2.5 },
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                                bgcolor: '#fafafa',
                            },
                        }}
                    >
                        {/* 아이콘 */}
                        <Box sx={{ mr: 2 }}>{item.icon}</Box>

                        {/* 메뉴명 */}
                        <Typography
                            sx={{
                                flex: 1,
                                fontSize: { xs: 14, sm: 15 },
                                fontWeight: 500,
                                color: item.disabled ? '#999' : '#222',
                            }}
                        >
                            {item.label}
                        </Typography>

                        {/* 화살표 */}
                        <ChevronRightIcon sx={{ fontSize: 20, color: '#ccc' }} />
                    </Box>
                    {index < menuItems.length - 1 && <Divider />}
                </Box>
            ))}
        </Box>
    );
};

export default MenuListCard;
